import Loading from '@/components/Loading';
import {
  classificationTypes,
  TreatmentFromForm,
  treatmentMetadataFields,
  updateNumberToString,
} from '@/components/Studies/Treatments/Treatment.utils';
import Banner from '@/components/UI/Banner';
import InvalidMetadataBanner from '@/components/UI/InvalidMetadataBanner/InvalidMetadataBanner';
import Button from '@/components/UI/Button';
import ControlledInputWithUnits from '@/components/UI/FormElements/ControlledInputWithUnits';
import { MetadataFieldBuilderReturn } from '@/components/UI/FormFields/FormField.utils';
import FormRender from '@/components/UI/FormRender';
import Select from '@/components/UI/Select';
import type { SelectOption } from '@/components/UI/Select/Select';
import { formatNumber } from '@/helpers';
import { _isEmptyString, _isNotBlank, _isNotEmpty, _notNil, uuid } from '@/littledash';
import type { ID } from '@/model/Common.model';
import type { MetadataField } from '@/model/Metadata.model';
import type {
  ClassificationType,
  Treatment,
  TreatmentField,
  TreatmentMethod,
  TreatmentVolumeUnit,
} from '@/model/Treatment.model';
import type { StudyApiId } from '@/model/Study.model';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import { useFetchEntity } from '@/support/Hooks/fetch';
import { ErrorMessage } from '@hookform/error-message';
import { FC, useEffect, useMemo, useState } from 'react'; //@ts-expect-error - old hook form - replace with `react-hook-form@latest`
import { Control, FormProvider, useController, useForm, useWatch } from 'react-hook-form';
import {
  dosingMethodOptions,
  dosingMethods,
  getDefaults,
  isValidMetadataValue,
  validateTreatmentMetadata,
} from './AddTreatmentForm.utils';
import type { TreatmentForm, TreatmentFormField, TreatmentFormUnit } from './TreatmentForm.model';
import { TreatmentLookup } from './TreatmentLookup';
import { validMultiSelectValue } from '@/components/Studies/Create/Show.utils';
import { useSelector } from 'react-redux';
import { State } from '@/model/State.model';
import Radio from '@/components/UI/FormElements/Radio';

export interface AddTreatmentFormProps {
  treatment: Treatment;
  study?: { api_id: StudyApiId };
  metadata: Array<MetadataField>;
  onSubmit: (treatment: Treatment, edit: boolean, treatmentId?: ID) => void;
  onCancel: () => void;
  classificationType: ClassificationType;
}

interface TreatmentExtended extends Treatment {
  meta: Record<string, string>;
}

export const handleTreatmentSubmit = async (
  treatmentData: Treatment & Record<string, TreatmentField>,
  metaFieldValues: MetadataFieldBuilderReturn[],
  treatment: Treatment,
  edit: boolean,
  onSubmit: (updatedTreatment: Treatment, edit: boolean, treatmentId?: ID) => void
) => {
  const dosingMethodValues = treatmentData[treatmentData.type];
  delete treatmentData[treatmentData.type];

  const multiSelectFields = metaFieldValues.reduce((acc, metaField) => {
    if (metaField.type === 'multi_select' || metaField.type === 'lookup_multi_select') {
      acc.add(metaField.slug);
    }
    return acc;
  }, new Set<string>());

  // If multi & lookup multi select fields have no values, remove them before creating treatment meta
  const meta = Object.entries(treatmentData?.meta ?? {}).reduce<Record<string, string>>((acc, [key, value]) => {
    if (multiSelectFields.has(key) && !validMultiSelectValue(value)) {
      return acc;
    }
    return { ...acc, [key]: value };
  }, {});

  const updatedTreatment: TreatmentExtended = { temp_id: treatment?.temp_id ?? uuid(), ...treatmentData, meta };

  if (_isNotEmpty(dosingMethodValues)) {
    updatedTreatment.fields = updateNumberToString([...Object.values(dosingMethodValues)]);
  }

  onSubmit(updatedTreatment, edit, treatment?.api_id);

  return updatedTreatment;
};

const AddTreatmentForm: FC<AddTreatmentFormProps> = ({
  treatment,
  study,
  metadata,
  onSubmit,
  onCancel,
  classificationType,
}) => {
  const diseaseInductionEnabled = useSelector((state: State) => state?.team?.features?.disease_induction ?? false);
  const volumeUnitEnabled = useSelector((state: State) => state?.team?.features?.treatment_volume_unit ?? false);
  const [hasMovedAnimals, setHasMovedAnimals] = useState<boolean>(false);

  const { invalidFields: initialInvalidFields, hasInvalidFields: initialHasInvalidFields } = useMemo(
    () => validateTreatmentMetadata(treatment, metadata),
    [treatment, metadata]
  );

  // Track which invalid fields have been corrected by the user
  const [correctedFields, setCorrectedFields] = useState<Set<string>>(new Set());

  // Compute metadata validation state
  const { metadataBySlug, invalidFields, hasInvalidFields, cleanedTreatment } = useMemo(() => {
    // Create a map of metadata fields by slug for validation
    const metadataBySlug = metadata.reduce<Record<string, MetadataField>>((acc, meta) => {
      acc[meta.slug] = meta;
      return acc;
    }, {});

    // Filter out corrected fields from the invalid fields
    const invalidFields = Object.entries(initialInvalidFields).reduce<Record<string, { title: string; slug: string }>>(
      (acc, [slug, value]) => {
        if (!correctedFields.has(slug)) {
          acc[slug] = value;
        }
        return acc;
      },
      {}
    );

    const hasInvalidFields = _isNotEmpty(Object.keys(invalidFields));

    // Clean treatment by removing invalid metadata
    const cleanedTreatment =
      initialHasInvalidFields && _notNil(treatment)
        ? {
            ...treatment,
            metadata: treatment.metadata?.filter((meta) => !Object.keys(initialInvalidFields).includes(meta.slug)),
          }
        : treatment;

    return { metadataBySlug, invalidFields, hasInvalidFields, cleanedTreatment };
  }, [metadata, initialInvalidFields, correctedFields, treatment, initialHasInvalidFields]);

  const defaultTreatmentMethod: TreatmentMethod = classificationType === 'disease_induction' ? 'custom' : 'volume';

  const [selectedTreatmentMethod, setSelectedTreatmentMethod] = useState<TreatmentMethod>(
    treatment?.type ?? defaultTreatmentMethod
  );

  const [selectedTreatmentClassification, setSelectedTreatmentClassification] = useState<ClassificationType>(
    treatment?.treatment_classification ?? classificationType
  );

  const [selectedVolumeUnit, setSelectedVolumeUnit] = useState<TreatmentVolumeUnit>(
    treatment?.treatment_volume_unit ?? (treatment?.calculates?.unit?.display_unit as TreatmentVolumeUnit) ?? 'µL'
  );

  const metaFieldValues = treatmentMetadataFields(metadata, cleanedTreatment);
  const treatmentClassification = classificationTypes[classificationType];

  const {
    entity: treatmentForm,
    entityLoading: treatmentFormLoading,
    entityUpdating: treatmentFormUpdating,
    fetchEntity: fetchTreatmentForm,
  } = useFetchEntity<TreatmentForm>({
    entityType: 'treatmentForm',
    params: { treatmentMethod: selectedTreatmentMethod },
  });

  const { invoke: checkHasMovedAnimals } = useApiHook({
    endpoint: 'GET /api/v1/studies/{studyApiId}/treatments/{treatmentApiId}/has-moved-animals',
    invokeOnInit: false,
    options: { onError: { toast: false, capture: false, throw: false } },
  });

  const edit = _isNotEmpty(treatment);

  const formMethods = useForm<TreatmentFromForm>({
    mode: 'onChange',
  });

  const {
    handleSubmit,
    control,
    reset,
    getValues,
    formState: { isSubmitting, errors },
  } = formMethods;
  const {
    field: { onBlur: treatmentOnBlur, onChange: treatmentOnChange, value: treatmentValue },
  } = useController({
    control,
    defaultValue: treatment?.treatment_type ?? null,
    name: 'treatment_type',
    rules: { required: `${treatmentClassification.key} is required` },
  });

  const {
    field: { onBlur: dosingMethodOnBlur, onChange: dosingMethodOnChange, value: dosingMethodValue },
  } = useController({
    control,
    name: 'type',
    defaultValue: treatment?.type ?? dosingMethodOptions[0].value,
    rules: { required: true },
  });

  const {
    field: { onChange: treatmentClassificationOnChange },
  } = useController({
    control,
    name: 'treatment_classification',
    defaultValue: treatment?.treatment_classification ?? 'treatment',
    rules: { required: 'Treatment Classification is required' },
  });

  const {
    field: { onBlur: treatmentVolumeUnitOnBlur, onChange: treatmentVolumeUnitOnChange },
  } = useController({
    control,
    name: 'treatment_volume_unit',
    rules: { required: 'Treatment volume unit is required' },
  });

  const classificationTypeOptions = Object.entries(classificationTypes)
    .filter(([key]) => key !== 'disease_induction')
    .map(([key, value]) => ({
      value: key as ClassificationType,
      label: value.title,
      sublabel: value.sublabel,
    }));

  // Watch the invalid field values to trigger re-evaluation when they change
  const { invalidFieldSlugs, watchFields } = useMemo(() => {
    const slugs = Object.keys(initialInvalidFields);
    return {
      invalidFieldSlugs: slugs,
      watchFields: slugs.map((slug) => `meta.${slug}`),
    };
  }, [initialInvalidFields]);
  const watchedInvalidFields = useWatch({ name: watchFields, control });

  // Check if invalid fields have been corrected when form values change
  useEffect(() => {
    const allValues = getValues();
    const watchedMeta = allValues?.meta;

    if (!initialHasInvalidFields || !_notNil(watchedMeta)) {
      return;
    }

    // Use functional update to avoid stale closure issues and remove correctedFields from deps
    setCorrectedFields((prevCorrectedFields) => {
      const newCorrectedFields = new Set(prevCorrectedFields);

      invalidFieldSlugs.forEach((slug) => {
        const currentValue = watchedMeta[slug];
        const metadataField = metadataBySlug[slug];

        // Only consider a field "corrected" if user has selected an actual valid value
        // Empty/null values should not be considered corrected
        const hasValue = _isNotBlank(currentValue);

        if (_notNil(metadataField) && hasValue) {
          const isValid = isValidMetadataValue(currentValue, metadataField);

          if (isValid) {
            newCorrectedFields.add(slug);
          } else {
            newCorrectedFields.delete(slug);
          }
        } else {
          // If value is empty/null, ensure it's not marked as corrected
          newCorrectedFields.delete(slug);
        }
      });

      // Only update if the set has changed (React will skip the update if we return the same reference)
      if (
        newCorrectedFields.size !== prevCorrectedFields.size ||
        ![...newCorrectedFields].every((f) => prevCorrectedFields.has(f))
      ) {
        return newCorrectedFields;
      }
      return prevCorrectedFields;
    });
  }, [watchedInvalidFields, initialHasInvalidFields, metadataBySlug, getValues, invalidFieldSlugs]);

  useEffect(() => {
    if (_isNotEmpty(selectedTreatmentMethod)) {
      dosingMethodOnChange(selectedTreatmentMethod);
      fetchTreatmentForm();
    }
  }, [selectedTreatmentMethod]);

  useEffect(() => {
    if (_isNotEmpty(selectedTreatmentClassification)) {
      treatmentClassificationOnChange(selectedTreatmentClassification);
    }
  }, [selectedTreatmentClassification]);

  useEffect(() => {
    if (_isNotEmpty(selectedVolumeUnit)) {
      treatmentVolumeUnitOnChange(selectedVolumeUnit);
    }
  }, [selectedVolumeUnit]);

  useEffect(() => {
    if (edit && _notNil(treatment?.api_id) && _notNil(study?.api_id)) {
      checkHasMovedAnimals({
        path: {
          studyApiId: study.api_id,
          treatmentApiId: treatment.api_id,
        },
      }).then((result) => {
        if (result?.type === 'success') {
          setHasMovedAnimals(result.body?.data ?? false);
        }
      });
    }
  }, [edit, treatment?.api_id, study?.api_id]);

  useEffect(() => {
    if (!treatmentFormLoading && _notNil(treatmentForm)) {
      const formattedFields =
        cleanedTreatment?.fields?.map((field) => {
          if (_notNil(field?.default_display_value)) {
            field.default_display_value = formatNumber(field.default_display_value);
          } else if (_notNil(field?.default_value)) {
            field.default_value = _isEmptyString(field.default_value ?? '') ? '' : formatNumber(field.default_value);
          }
          return field;
        }) ?? [];

      const formattedTreatments = {
        ...cleanedTreatment,
        fields: formattedFields,
      };

      const currentFormValues = getValues(['treatment_type', 'type', 'meta']);
      const resetValues = {
        ...currentFormValues,
        ...getDefaults(treatmentForm.name, treatmentForm.fields, selectedTreatmentMethod, formattedTreatments),
      };

      if (hasInvalidFields) {
        if (!_notNil(resetValues.meta)) {
          resetValues.meta = {};
        }
        Object.keys(invalidFields).forEach((fieldSlug) => {
          resetValues.meta[fieldSlug] = null;
        });
      }

      reset(resetValues);
    }
  }, [treatmentForm, cleanedTreatment, hasInvalidFields, invalidFields]);

  return !treatmentFormLoading && _notNil(treatmentForm) ? (
    <FormProvider {...formMethods}>
      <form
        onSubmit={handleSubmit((formData: Treatment & Record<string, TreatmentField>) =>
          handleTreatmentSubmit(formData, metaFieldValues, treatment, edit, onSubmit)
        )}
        className="center pb0 ui-card"
        style={{ maxWidth: '500px' }}
        noValidate
        data-test-component="AddTreatmentForm"
        data-test-element="form"
      >
        <div className="ph3 pt3">
          {edit && hasMovedAnimals && (
            <Banner info dismiss={false} className="mb3">
              Editing is disabled because this treatment was administered to an animal that is no longer in this study.
            </Banner>
          )}
          {hasInvalidFields && (
            <InvalidMetadataBanner
              invalidFields={Object.fromEntries(
                Object.entries(invalidFields).map(([key, value]) => [key, value.title])
              )}
            />
          )}
          {diseaseInductionEnabled && classificationType !== 'disease_induction' && (
            <div className="mb3" data-test-element="treatment-classification-container">
              {classificationTypeOptions.map((o) => (
                <Radio
                  value={o.value}
                  id={o.value}
                  label={o.label}
                  key={o.label}
                  sublabel={o.sublabel}
                  disabled={treatment?.in_use === true || hasMovedAnimals}
                  checked={o.value === selectedTreatmentClassification}
                  onChange={(e) => setSelectedTreatmentClassification(e.target.value as ClassificationType)}
                  dataTestId={`${o.label}_radio_test`}
                />
              ))}
            </div>
          )}
          <div className="mb3" data-test-element="treatment-lookup-container">
            <label>Name (required)</label>
            <TreatmentLookup
              value={treatmentValue}
              onChange={treatmentOnChange}
              onBlur={treatmentOnBlur}
              classificationType={classificationType}
              isDisabled={hasMovedAnimals}
            />
            <ErrorMessage
              name="treatment_type"
              errors={errors}
              render={({ message }) => <small className="red mb3">{message}</small>}
            />
          </div>
          <div className="mb3" data-test-element="dosing-method-container">
            <label>Dosing Method</label>
            <Select
              defaultValue={dosingMethods?.[selectedTreatmentMethod]}
              disabled={treatment?.in_use === true || hasMovedAnimals}
              options={dosingMethodOptions}
              onChange={(newValue) => setSelectedTreatmentMethod((newValue as SelectOption<TreatmentMethod>).value)}
              onBlur={dosingMethodOnBlur}
              className="w-100"
              isMulti={false}
            />
          </div>
          <div className="justify-between flex" style={{ gap: 10 }} data-test-element="dosing-method-fields-container">
            {!treatmentFormUpdating && _notNil(treatmentForm.fields) ? (
              (treatmentForm?.fields ?? []).map((field) => (
                <DosingMethodField
                  key={`${treatmentForm?.name}.${field.name}`}
                  id={`${treatmentForm?.name}.${field.name}`}
                  dosingType={dosingMethodValue}
                  control={control}
                  field={field}
                  isSubmitting={isSubmitting === true || hasMovedAnimals}
                  unitsDisabled={treatment?.in_use === true || hasMovedAnimals}
                  errors={errors}
                  units={field.units.filter((unit) => unit.unit_type === field.name)}
                />
              ))
            ) : (
              <div className="flex align-center w-100 pt5-ns">
                <Loading txt="" loader />
              </div>
            )}
          </div>
          {!treatmentFormUpdating && _notNil(treatmentForm.calculates) && volumeUnitEnabled && (
            <div className="mb3" data-test-element="dosing-volume-unit">
              <label>{treatmentForm.calculates.label}</label>
              <Select
                defaultValue={treatmentForm.calculates.options?.find((opt) => opt.value === selectedVolumeUnit)}
                options={treatmentForm.calculates.options ?? []}
                onChange={(newValue) => {
                  if (_notNil(newValue)) {
                    setSelectedVolumeUnit((newValue as SelectOption<TreatmentVolumeUnit>).value);
                  }
                }}
                onBlur={treatmentVolumeUnitOnBlur}
                isMulti={false}
              />
            </div>
          )}
          <div className="pb3" data-test-element="metadata-fields-container">
            <FormRender
              fields={metaFieldValues}
              disabled={hasMovedAnimals}
              invalidFields={new Set(Object.keys(invalidFields))}
            />
          </div>
        </div>
        <div className="pa3 flex self-end bt b--moon-gray w-100">
          <Button
            submit
            loading={isSubmitting}
            loadingText={`${edit ? 'Editing' : 'Adding'} ${treatmentClassification.key}`}
            testId="add-or-update-treatment"
            disabled={hasMovedAnimals || hasInvalidFields}
          >
            {edit ? 'Update' : 'Add'} {treatmentClassification.key}
          </Button>
          <Button plain className="ml3" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </form>
    </FormProvider>
  ) : (
    <></>
  );
};

interface DosingMethodFieldProps {
  id: string;
  control: Control<Treatment>;
  dosingType: TreatmentMethod;
  field: TreatmentFormField;
  errors: Record<string, unknown>;
  isSubmitting: boolean;
  units: Array<TreatmentFormUnit>;
  unitsDisabled?: boolean;
}

const DosingMethodField: FC<DosingMethodFieldProps> = ({
  id,
  control,
  dosingType,
  field,
  errors,
  isSubmitting,
  units,
  unitsDisabled,
}) => (
  <div
    className="w-50"
    data-test-component="DosingMethodField"
    data-test-element="container"
    data-test-key={field.name}
  >
    <label>
      {field.label} {field.required && '(required)'}
    </label>
    <fieldset className="w-100 relative flex justify-between">
      <ControlledInputWithUnits
        id={id}
        classes="w-100"
        field={field}
        selectRequired={dosingType === 'custom'}
        disabled={isSubmitting}
        hasError={_notNil(errors?.[id])}
        units={units}
        control={control}
        errors={errors}
        unitsDisabled={unitsDisabled}
        tooltip={id === 'custom.volume' ? 'When marked as required a value must be entered at dosing' : undefined}
      />
    </fieldset>
  </div>
);

export default AddTreatmentForm;
